/**
 * <NextSessionSection> — « Prochaine séance » sur l'accueil parent (template).
 *
 * La toute prochaine séance en grand, SEULE dans le cadre en dégradé : le temps fort de l'accueil,
 * c'est elle. Les séances suivantes ne sont pas ici — elles vivent plus bas, dans « À venir »
 * (`<UpcomingSessionsSection>`), sous les quatre gestes et à côté du dernier bilan.
 *
 * Sans séance, la section reste à sa place avec « Aucune séance à venir » : c'est la réponse que
 * le parent vient chercher en ouvrant l'app, elle ne disparaît pas comme « À faire ».
 *
 * Aucun menu « ⋮ » : toucher le bloc ouvre la feuille des actions de CETTE séance, la même que dans
 * « Tous mes lives ». De 15 min avant le début à la fin, le bloc porte aussi « Rejoindre » — le
 * même geste que la ligne de la feuille, quand l'appelant l'offre (`onJoinSession`).
 *
 * **Sur l'accueil de l'élève** (`voice="student"`, `joinDisplay="always"`) : l'intitulé est le sien
 * (`NextLiveSessionBanner.js`), et son « GO ! » reste sur la carte toute la journée du live comme
 * les autres jours. Sans prochain live, son accueil ne monte pas la section : l'ancien bandeau ne
 * rendait rien.
 */
import React from "react"

import { CardList } from "../../molecules/CardList"
import { HighlightFrame } from "../../molecules/HighlightFrame"
import { List } from "../../molecules/List"
import { NextSessionHero, type NextSessionJoinDisplay } from "../../organisms/NextSessionHero"
import { type Voice } from "../../utils/voice"
import { type UILiveSession } from "../../../ports/primary/ui/types"

const TEXTS: Record<Voice, { title: string }> = {
    parent: { title: "Prochaine séance" },
    student: { title: "Prochaine session LIVE" },
}

export type NextSessionSectionProps = {
    /** La prochaine séance — `null` quand il n'y en a aucune. */
    session: UILiveSession | null
    /** Ouvre les actions de la séance. Absent → rien ne presse. */
    onSelectSession?: (session: UILiveSession) => void
    /** « Rejoindre », dans la fenêtre d'entrée de la séance. Absent → pas de bouton. */
    onJoinSession?: (session: UILiveSession) => void
    /** `"window"` (défaut) : le bouton dans la fenêtre d'entrée. `"always"` : le « GO ! » de l'élève. */
    joinDisplay?: NextSessionJoinDisplay
    /** À qui la section parle — `"parent"` par défaut. */
    voice?: Voice
    testID?: string
}

export function NextSessionSection(props: NextSessionSectionProps) {
    const { session, onSelectSession, onJoinSession, joinDisplay, voice = "parent", testID } = props

    return (
        <List title={TEXTS[voice].title} emptyMessage="Aucune séance à venir" isEmpty={session == null} testID={testID}>
            {session != null && (
                <HighlightFrame>
                    <CardList>
                        <NextSessionHero
                            session={session}
                            onPress={onSelectSession != null ? () => onSelectSession(session) : undefined}
                            onJoin={onJoinSession != null ? () => onJoinSession(session) : undefined}
                            joinDisplay={joinDisplay}
                            voice={voice}
                        />
                    </CardList>
                </HighlightFrame>
            )}
        </List>
    )
}
